import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileUp, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { leadsApi, type ImportResult } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils';

export function ImportLeadsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [drag, setDrag] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const mut = useMutation({
    mutationFn: (f: File) => leadsApi.import(f),
    onSuccess: (r) => {
      setResult(r);
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const reset = () => {
    setFile(null);
    setResult(null);
    mut.reset();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Importar leads (CSV)</DialogTitle>
          <DialogDescription>
            Aceita o export da tabela de webhooks (coluna <code>raw_body</code>) ou um CSV simples com colunas <code>name, phone, email, country, product_codename</code>.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div
            className={cn('flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 text-center transition-colors', drag && 'border-primary bg-primary/5')}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
            onClick={() => inputRef.current?.click()}
          >
            <FileUp className="size-8 text-muted-foreground" />
            {file ? (
              <p className="text-sm font-medium">{file.name} <span className="text-muted-foreground">({(file.size / 1024).toFixed(0)} KB)</span></p>
            ) : (
              <p className="text-sm text-muted-foreground">Arraste o arquivo aqui ou clique para selecionar</p>
            )}
            <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </div>
        ) : (
          <div className="space-y-3">
            <Alert>
              <CheckCircle2 className="text-success" />
              <AlertTitle>Importação concluída</AlertTitle>
              <AlertDescription>
                {result.totalRows} linhas lidas · {result.created} criados · {result.updated} atualizados · {result.skipped} ignorados (não ligar)
              </AlertDescription>
            </Alert>
            {result.errors.length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle />
                <AlertTitle>{result.errors.length} linha(s) com erro</AlertTitle>
                <AlertDescription>
                  <ScrollArea className="mt-2 h-32 w-full rounded border bg-background/50 p-2">
                    <ul className="space-y-1 text-xs">
                      {result.errors.map((e, i) => <li key={i}>Linha {e.row ?? (e.index != null ? e.index + 2 : '?')}: {e.message}</li>)}
                    </ul>
                  </ScrollArea>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {mut.error && <p className="text-sm text-destructive">{mut.error instanceof ApiError ? mut.error.message : 'Falha ao importar'}</p>}

        <DialogFooter>
          {result ? (
            <Button onClick={() => onOpenChange(false)}>Fechar</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button disabled={!file || mut.isPending} onClick={() => file && mut.mutate(file)}>
                {mut.isPending && <Loader2 className="animate-spin" />} Importar
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
